import { Buttons, Image, Scroller, Slider } from '@cian/slider';
import { IconActionFullscreen16 } from '@cian/ui-kit-design-tokens/icons';
import { type ComponentRef, type KeyboardEvent, useEffect, useRef } from 'react';

import type { IBuildingPhoto } from 'shared/types/buildingCard';

import { useMouseSwipe } from '../../hooks';
import { GalleryThumbs } from '../GalleryThumbs';

import style from './style.css';

export interface IGallerySliderProps {
  photos: IBuildingPhoto[];
  /** alt по фото */
  alts: string[];
  /** Фото, с которого начинается показ */
  initialSlide: number;
  /** Фото, к которому перейти, если его сменили снаружи (в полноэкранном режиме) */
  activeSlide?: number;
  isFullscreen?: boolean;
  onSlideChange?(index: number): void;
  onFullscreenClick?(): void;
}

/** Слайдер фото с миниатюрами — как fotorama монолита: loop, клавиатура, свайп, вписывание фото */
export const GallerySlider = ({
  photos,
  alts,
  initialSlide,
  activeSlide,
  isFullscreen,
  onSlideChange,
  onFullscreenClick,
}: IGallerySliderProps) => {
  const sliderRef = useRef<ComponentRef<typeof Slider>>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialSlide > 0) {
      sliderRef.current?.scrollTo(initialSlide, { behavior: 'instant' });
    }
    if (isFullscreen) {
      containerRef.current?.focus({ preventScroll: true });
    }
    // Стартовое фото выставляется один раз при появлении слайдера
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const slider = sliderRef.current;

    if (activeSlide !== undefined && slider && slider.currentSlide !== activeSlide) {
      slider.scrollTo(activeSlide, { behavior: 'instant' });
    }
  }, [activeSlide]);

  const swipeHandlers = useMouseSwipe({
    onPrev: () => sliderRef.current?.scrollPrev(),
    onNext: () => sliderRef.current?.scrollNext(),
  });

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      sliderRef.current?.scrollPrev();
    } else if (event.key === 'ArrowRight') {
      sliderRef.current?.scrollNext();
    }
  };

  return (
    <div
      ref={containerRef}
      className={isFullscreen ? `${style['container']} ${style['container--fullscreen']}` : style['container']}
      data-name="GallerySlider"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <Slider ref={sliderRef} loop behavior="smooth" onSlideChange={onSlideChange}>
        <div className={style['main']} data-name="GallerySlides" {...swipeHandlers}>
          <Scroller>
            {photos.map(({ fullUrl }, index) => (
              <Image
                key={`${fullUrl}-${index}`}
                src={fullUrl}
                alt={alts[index]}
                objectFit="contain"
                priority={index === initialSlide}
              />
            ))}
          </Scroller>
          <Buttons size="M" />
          {onFullscreenClick && (
            <button
              type="button"
              className={style['fullscreen']}
              title="Открыть на весь экран"
              onClick={onFullscreenClick}
            >
              <IconActionFullscreen16 color="icon-inverted-default" />
            </button>
          )}
        </div>
        <GalleryThumbs photos={photos} alts={alts} />
      </Slider>
    </div>
  );
};
